import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import logoIcon from '../assets/logo-icon-exact.png';
import allQuestions from './data/questions';
import SubscribeModal from '../components/SubscribeModal';
import { getMissedQuestions, getWeakestTopic } from '../quizStats';
import Footer from '../components/Footer';
import ArcFlash from './ArcFlash';

const FREE_SECTION      = 'sec4';
const FREE_SECTION_MILL = 'mill_pumps';
const FREE_LIMIT        = 3;
const STRIPE_MONTHLY = 'https://buy.stripe.com/4gMaEW7lb0sOcPDa7q5ZC04';
const STRIPE_ANNUAL  = 'https://buy.stripe.com/3cIaEWgVL1wSg1P3J25ZC03';
const RESUME_STORAGE_KEY = 'trp_quiz_resume_v1';
const DAILY_GOAL_STORAGE_KEY = 'trp_daily_goal_target';
const DEFAULT_DAILY_GOAL_TARGET = 20;
const QUIZ_VAULT_STORAGE_KEY = 'trp_quiz_vault_progress_v1';
const QUIZ_VAULT_PENDING_KEY = 'trp_pending_vault_quiz';
const QUIZ_VAULT_SIZE = 25;
const QUIZ_VAULT_TRACKS = 12;
const QUIZ_VAULT_PASS_PERCENT = 70;

const QUIZ_VAULT_LICENCES = [
  { value: 'master', label: 'Master Electrician' },
  { value: '309a', label: '309A Electrician' },
  { value: '442a', label: '442A Electrician' },
];

function readDailyGoalTarget() {
  const raw = localStorage.getItem(DAILY_GOAL_STORAGE_KEY);
  const parsed = Number.parseInt(raw || '', 10);
  if (!Number.isFinite(parsed) || parsed < 1) return DEFAULT_DAILY_GOAL_TARGET;
  return parsed;
}

const EXAM_SIM_PRESETS = [
  { questions: 20, minutes: 48 },
  { questions: 40, minutes: 96 },
  { questions: 60, minutes: 144 },
  { questions: 100, minutes: 240 },
];
const EXAM_SIM_QUESTION_OPTIONS = EXAM_SIM_PRESETS.map(preset => preset.questions);
const EXAM_SIM_TIME_BY_QUESTION = {
  '20': 48,
  '40': 96,
  '60': 144,
  '100': 240,
};
const QUIZ_LICENCE_OPTIONS = [
  { value: '442a', label: '442A Industrial Electrician' },
  { value: '309a', label: '309A Construction & Maintenance Electrician' },
  { value: '309c', label: '309C Domestic & Rural Electrician' },
  { value: 'master', label: 'Master Electrician' },
];

const LICENCE_LABELS = {
  master: 'Ontario Master Electrician',
  '309a': '309A Construction & Maintenance Electrician',
  '309c': 'Electrician — Domestic and Rural (309C)',
  '442a': '442A Industrial Electrician',
  '433a': '433A Industrial Mechanic / Millwright',
  gas: 'Gas Fitter (B149.1)',
};

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function normalizeDisplayText(value) {
  if (typeof value !== 'string') return value;
  return value
    .replace(/ï¿½C/g, '°C')
    .replace(/ï¿½F/g, '°F')
    .replace(/�C/g, '°C')
    .replace(/�F/g, '°F')
    .replace(/mi(?:ï¿½|¿½|�)/g, 'm2')
    .replace(/([A-Za-z])(?:ï¿½|¿½|�)(?=\b|\s|\/)/g, '$12')
    .replace(/(\d)(?:ï¿½|�)(?=[CF])/g, '$1°')
    .replace(/(\d)(?:ï¿½|�)(?=\s|$)/g, '$1°');
}

function normalizeCodeTerminology(value, licenceGroup) {
  const normalized = normalizeDisplayText(value);
  if (licenceGroup === 'master' || typeof normalized !== 'string') return normalized;
  return normalized
    .replace(/\bOESC\b/g, 'CEC')
    .replace(/Ontario Electrical Safety Code/g, 'Canadian Electrical Code');
}

function formatQuestionForQuiz(q, licenceGroup, shouldShuffleOptions = true) {
  const opts = q.options.map((text, i) => ({ text: normalizeCodeTerminology(text, licenceGroup), correct: i === q.correct }));
  const aotaIdx = opts.findIndex(o => /(all|any|none) (of )?the above/i.test(o.text));
  const shuffled = !shouldShuffleOptions
    ? opts
    : aotaIdx !== -1
      ? [...shuffle(opts.filter((_, i) => i !== aotaIdx)), opts[aotaIdx]]
      : shuffle(opts);
  return {
    ...q,
    topicLabel: normalizeCodeTerminology(q.topicLabel, licenceGroup),
    question: normalizeCodeTerminology(q.question, licenceGroup),
    explanation: normalizeCodeTerminology(q.explanation, licenceGroup),
    codeRef: normalizeCodeTerminology(q.codeRef, licenceGroup),
    shuffledOptions: shuffled,
  };
}

function withShuffledOptions(q, licenceGroup) {
  return formatQuestionForQuiz(q, licenceGroup, true);
}

function withFixedOptions(q, licenceGroup) {
  return formatQuestionForQuiz(q, licenceGroup, false);
}

function getVaultProgressStorageId(userId) {
  return userId || 'guest';
}

function readVaultProgress(userId) {
  try {
    const raw = localStorage.getItem(QUIZ_VAULT_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed?.[getVaultProgressStorageId(userId)] || {};
  } catch {
    return {};
  }
}

function writeVaultProgress(userId, progress) {
  try {
    const raw = localStorage.getItem(QUIZ_VAULT_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    parsed[getVaultProgressStorageId(userId)] = progress;
    localStorage.setItem(QUIZ_VAULT_STORAGE_KEY, JSON.stringify(parsed));
  } catch {}
}

function dedupeQuestions(pool) {
  const seen = new Set();
  return pool.filter(question => {
    if (seen.has(question.qid)) return false;
    seen.add(question.qid);
    return true;
  });
}

function buildQuizVaultQuestions(licence) {
  const required = QUIZ_VAULT_SIZE * QUIZ_VAULT_TRACKS;
  const primaryPool = dedupeQuestions(allQuestions.filter(q => q.licences.includes(licence)));
  if (licence !== 'master' || primaryPool.length >= required) {
    return primaryPool.slice(0, required);
  }

  const fallbackPool = dedupeQuestions([
    ...primaryPool,
    ...allQuestions.filter(q => q.licences.includes('309a') || q.licences.includes('442a')),
  ]);
  return fallbackPool.slice(0, required);
}

function getQuizVaultTracks(licence) {
  const pool = buildQuizVaultQuestions(licence);
  return Array.from({ length: QUIZ_VAULT_TRACKS }, (_, index) => {
    const start = index * QUIZ_VAULT_SIZE;
    return pool.slice(start, start + QUIZ_VAULT_SIZE);
  }).filter(track => track.length === QUIZ_VAULT_SIZE);
}

function getQuizVaultTrackProgress(progress, licence, trackNumber) {
  return progress?.[licence]?.[trackNumber] || null;
}

function isQuizVaultTrackUnlocked(progress, licence, trackNumber) {
  if (trackNumber === 1) return true;
  const previous = getQuizVaultTrackProgress(progress, licence, trackNumber - 1);
  return Number(previous?.bestPct || 0) >= QUIZ_VAULT_PASS_PERCENT;
}

function prepareQuestions(topic, count, licenceGroup, hasFullAccess) {
  let pool;
  // Virtual pools for the Quick Quiz tabs
  const QUICK_POOLS = {
    quick_oesc:   q => q.licences.includes('master') && (q.topic === 'oesc' || (q.topic.startsWith('sec') && q.topic !== 'sec_master')),
    quick_gas:    q => q.licences.includes('gas'),
    quick_ohsa:   q => q.topic.startsWith('ohsa_'),
    quick_mill:   q => q.licences.includes('433a'),
    quick_regs:   q => q.topic === 'reg570' || q.topic === 'elec_act_viii',
  };

  if (QUICK_POOLS[topic]) {
    pool = allQuestions.filter(QUICK_POOLS[topic]);
  } else if (topic === 'all') {
    pool = allQuestions.filter(q => q.licences.includes(licenceGroup));
  } else {
    pool = allQuestions.filter(q => q.topic === topic);
  }

  // Full-access users get shuffled random questions.
  // Free users always see questions in file order — same questions every attempt.
  if (hasFullAccess) {
    pool = shuffle(pool);
  }

  const limit = count === 'all' ? pool.length : Math.min(parseInt(count), pool.length);
  return pool.slice(0, limit).map(q => withShuffledOptions(q, licenceGroup));
}

function Quiz({ session, darkMode, toggleDark, hasFullAccess, isSubscribed, arcFlashView: arcFlashRouteView = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const handleSignOut = async () => { await supabase.auth.signOut(); navigate('/'); };

  const [arcFlashView, setArcFlashView] = useState(Boolean(location.state?.arcFlashView || arcFlashRouteView));

  // ── Layout state ───────────────────────────────────────────────────────────
  const [elecOpen, setElecOpen]         = useState(true);
  const [openElecSub, setOpenElecSub]   = useState(null);
  const [activeTab, setActiveTab]       = useState('quiz');
  const [activeLicence, setActiveLicence] = useState('309a');
  const [vaultLicence, setVaultLicence] = useState('309a');
  const [modalOpen, setModalOpen]       = useState(false);
  const [drawerOpen, setDrawerOpen]     = useState(false);

  const closeDrawer = () => setDrawerOpen(false);

  const isMillwright  = activeLicence === '433a';
  const isGasFitter   = activeLicence === 'gas';
  const [arcFlashOpen, setArcFlashOpen] = useState(false);
  const practiceArcFlashLocked = !hasFullAccess && !isSubscribed;

  useEffect(() => {
    const nextArcFlashView = Boolean(location.state?.arcFlashView || arcFlashRouteView || new URLSearchParams(location.search).get('arcFlash') === '1');
    setArcFlashView(nextArcFlashView);
  }, [arcFlashRouteView, location.search, location.state]);

  const openArcFlashCourse = (moduleNumber) => {
    const params = new URLSearchParams(location.search);
    const normalizedModuleNumber = moduleNumber === 'final' ? 'exam' : moduleNumber;
    if (normalizedModuleNumber !== undefined && normalizedModuleNumber !== null) {
      params.set('m', String(normalizedModuleNumber));
      params.delete('module');
    } else {
      params.delete('m');
      params.delete('module');
    }
    params.set('arcFlash', '1');
    const nextQuery = params.toString();
    setArcFlashView(true);
    navigate(`/quiz${nextQuery ? `?${nextQuery}` : ''}`, { state: { arcFlashView: true } });
  };

  const ARC_FLASH_MODULES = [
    { key: 'arc-1', title: 'Module 1: What Is an Arc Flash?', locked: !hasFullAccess },
    { key: 'arc-2', title: 'Module 2: Electrical Injuries', locked: !hasFullAccess },
    { key: 'arc-3', title: 'Module 3: Standards & Regulations', locked: !hasFullAccess },
    { key: 'arc-4', title: 'Module 4: Electrically Safe Work', locked: !hasFullAccess },
    { key: 'arc-5', title: 'Module 5: Voltage Testing', locked: !hasFullAccess },
    { key: 'arc-6', title: 'Module 6: Risk Assessment', locked: !hasFullAccess },
    { key: 'arc-7', title: 'Module 7: Boundaries & Labels', locked: !hasFullAccess },
    { key: 'arc-8', title: 'Module 8: PPE & Planning', locked: !hasFullAccess },
    { key: 'arc-9', title: 'Module 9: Hazard Reduction', locked: !hasFullAccess },
    { key: 'arc-final', title: 'Final Exam', locked: !hasFullAccess },
  ];

  // ── Quiz state ─────────────────────────────────────────────────────────────
  const [phase, setPhase]             = useState('setup');
  const [selectedTopic, setTopic]     = useState('all');
  const [questionCount, setCount]     = useState('10');
  const [quizQuestions, setQuestions] = useState([]);
  const [current, setCurrent]         = useState(0);
  const [answers, setAnswers]         = useState([]);
  const [selected, setSelected]       = useState(null);
  const [revealed, setRevealed]       = useState(false);
  const [gradingMode, setGradingMode]         = useState('immediate');
  const [showFullReview, setShowFullReview]   = useState(false);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [showFlagModal, setShowFlagModal]     = useState(false);
  const [flagReason, setFlagReason]           = useState('');
  const [flagComment, setFlagComment]         = useState('');
  const [flagSubmitting, setFlagSubmitting]   = useState(false);
  const [flagSubmitted, setFlagSubmitted]     = useState(false);
  const [smartQuizLoading, setSmartQuizLoading] = useState(false);
  const [smartQuizMessage, setSmartQuizMessage] = useState('');
  const [showPaywallPromo, setShowPaywallPromo]       = useState(false);
  const [paywallPromoCode, setPaywallPromoCode]       = useState('');
  const [paywallPromoError, setPaywallPromoError]     = useState('');
  const [paywallPromoLoading, setPaywallPromoLoading] = useState(false);
  const [showExamSim, setShowExamSim]                 = useState(false);
  const [examSimLicence, setExamSimLicence]           = useState('309a');
  const [examSimQuestionCount, setExamSimQuestionCount] = useState('20');
  const [examPreset, setExamPreset]                   = useState(null);
  const [timeRemaining, setTimeRemaining]             = useState(0);
  const [resumeMeta, setResumeMeta]                   = useState(null);
  const [dailyAnswered, setDailyAnswered]             = useState(0);
  const [dailyGoalTarget, setDailyGoalTarget]         = useState(() => readDailyGoalTarget());
  const [dailyGoalLoading, setDailyGoalLoading]       = useState(false);
  const [recentScores, setRecentScores]               = useState([]);
  const [recentScoresLoading, setRecentScoresLoading] = useState(false);
  const [vaultProgress, setVaultProgress]             = useState(() => readVaultProgress(null));
  const [vaultQuizMeta, setVaultQuizMeta]             = useState(null);
  const timedAutoSubmitted                            = useRef(false);
  const selectedGradingMode = gradingMode === 'exam' ? 'exam' : 'immediate';
  const [paywallPlan, setPaywallPlan] = useState('monthly');
  const annualCheckoutUrl = session?.user?.id
    ? `${STRIPE_ANNUAL}?client_reference_id=${encodeURIComponent(session.user.id)}`
    : '/subscribe';
  const monthlyCheckoutUrl = session?.user?.id
    ? `${STRIPE_MONTHLY}?client_reference_id=${encodeURIComponent(session.user.id)}`
    : '/subscribe';
  const paywallCheckoutUrl = paywallPlan === 'annual' ? annualCheckoutUrl : monthlyCheckoutUrl;

  useEffect(() => {
    const isElectricianLicence = QUIZ_LICENCE_OPTIONS.some(opt => opt.value === activeLicence);
    if (isElectricianLicence) {
      setExamSimLicence(activeLicence);
    }
  }, [activeLicence]);

  useEffect(() => {
    if (QUIZ_VAULT_LICENCES.some(option => option.value === activeLicence)) {
      setVaultLicence(activeLicence);
    }
  }, [activeLicence]);

  useEffect(() => {
    setVaultProgress(readVaultProgress(session?.user?.id || null));
  }, [session]);

  const clearSavedQuiz = useCallback(() => {
    localStorage.removeItem(RESUME_STORAGE_KEY);
    setResumeMeta(null);
  }, []);

  const readSavedQuiz = useCallback(() => {
    const raw = localStorage.getItem(RESUME_STORAGE_KEY);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      if (!parsed || !Array.isArray(parsed.quizQuestions) || parsed.quizQuestions.length === 0) {
        return null;
      }
      const total = parsed.quizQuestions.length;
      return {
        snapshot: parsed,
        total,
        answered: Array.isArray(parsed.answers) ? parsed.answers.length : 0,
        topic: parsed.selectedTopic || 'all',
        licence: parsed.activeLicence || '309a',
        savedAt: parsed.savedAt || null,
      };
    } catch {
      return null;
    }
  }, []);

  const resumeSavedQuiz = useCallback(() => {
    const saved = readSavedQuiz();
    if (!saved) {
      clearSavedQuiz();
      return;
    }
    const s = saved.snapshot;
    const total = s.quizQuestions.length;
    const clampedCurrent = Number.isInteger(s.current)
      ? Math.max(0, Math.min(s.current, total - 1))
      : 0;

    setActiveLicence(s.activeLicence || '309a');
    setTopic(s.selectedTopic || 'all');
    setCount(s.questionCount || '10');
    setActiveTab(s.activeTab || 'quiz');
    setGradingMode(s.gradingMode || (localStorage.getItem('trp_grading_mode') || 'immediate'));
    setQuestions(s.quizQuestions);
    setCurrent(clampedCurrent);
    setAnswers(Array.isArray(s.answers) ? s.answers : []);
    setSelected(Number.isInteger(s.selected) ? s.selected : null);
    setRevealed(Boolean(s.revealed));
    setTimeRemaining(typeof s.timeRemaining === 'number' ? s.timeRemaining : 0);
    setExamPreset(s.examPreset || null);
    setShowFullReview(false);
    setShowSubmitConfirm(false);
    timedAutoSubmitted.current = false;
    setPhase('quiz');
    setResumeMeta({
      total,
      answered: Array.isArray(s.answers) ? s.answers.length : 0,
      topic: s.selectedTopic || 'all',
      licence: s.activeLicence || '309a',
      savedAt: s.savedAt || null,
    });
  }, [clearSavedQuiz, readSavedQuiz]);

  const handleGradingMode = (mode) => {
    setGradingMode(mode);
    localStorage.setItem('trp_grading_mode', mode);
  };

  // ── Auto-select topic from navigation state (e.g. home page free banner) ──
  useEffect(() => {
    if (location.state?.autoTopic) {
      const topic = location.state.autoTopic;
      setTopic(topic);
      if (topic === FREE_SECTION_MILL) setActiveLicence('433a');
      window.history.replaceState({}, '');
    }
  }, [location.state]);

  useEffect(() => {
    const saved = readSavedQuiz();
    if (!saved) {
      setResumeMeta(null);
      return;
    }
    setResumeMeta({
      total: saved.total,
      answered: saved.answered,
      topic: saved.topic,
      licence: saved.licence,
      savedAt: saved.savedAt,
    });
  }, [readSavedQuiz]);

  useEffect(() => {
    if (phase !== 'quiz' || !quizQuestions.length) return;
    const snapshot = {
      activeLicence,
      selectedTopic,
      questionCount,
      gradingMode,
      activeTab,
      quizQuestions,
      current,
      answers,
      selected,
      revealed,
      timeRemaining,
      examPreset,
      savedAt: Date.now(),
    };
    localStorage.setItem(RESUME_STORAGE_KEY, JSON.stringify(snapshot));
    setResumeMeta({
      total: quizQuestions.length,
      answered: answers.length,
      topic: selectedTopic,
      licence: activeLicence,
      savedAt: snapshot.savedAt,
    });
  }, [phase, quizQuestions, activeLicence, selectedTopic, questionCount, gradingMode, activeTab, current, answers, selected, revealed, timeRemaining, examPreset]);

  const loadSidebarInsights = useCallback(async () => {
    if (!session?.user?.id) {
      setDailyAnswered(0);
      setRecentScores([]);
      setDailyGoalLoading(false);
      setRecentScoresLoading(false);
      return;
    }

    setDailyGoalLoading(true);
    setRecentScoresLoading(true);

    const dayStart = new Date();
    dayStart.setHours(0, 0, 0, 0);
    const dayStartIso = dayStart.toISOString();

    const [dailyRes, recentRes] = await Promise.all([
      supabase
        .from('quiz_scores')
        .select('total')
        .eq('user_id', session.user.id)
        .gte('created_at', dayStartIso),
      supabase
        .from('quiz_scores')
        .select('topic, score, total, percentage, created_at')
        .eq('user_id', session.user.id)
        .order('created_at', { ascending: false })
        .limit(5),
    ]);

    if (dailyRes.error) {
      console.error('daily goal query failed:', dailyRes.error);
      setDailyAnswered(0);
    } else {
      const answered = (dailyRes.data || []).reduce((sum, row) => sum + (Number(row.total) || 0), 0);
      setDailyAnswered(answered);
    }

    if (recentRes.error) {
      console.error('recent scores query failed:', recentRes.error);
      setRecentScores([]);
    } else {
      setRecentScores(recentRes.data || []);
    }

    setDailyGoalLoading(false);
    setRecentScoresLoading(false);
  }, [session]);

  useEffect(() => {
    loadSidebarInsights();
  }, [loadSidebarInsights, phase]);

  useEffect(() => {
    setDailyGoalTarget(readDailyGoalTarget());
  }, []);

  // ── Timed exam: tick every second ────────────────────────────────────────
  useEffect(() => {
    if (gradingMode !== 'timed' || phase !== 'quiz' || timeRemaining <= 0) return;
    const id = setTimeout(() => setTimeRemaining(t => t - 1), 1000);
    return () => clearTimeout(id);
  }, [timeRemaining, gradingMode, phase]);

  // ── Timed exam: auto-submit when clock hits zero ──────────────────────────
  useEffect(() => {
    if (gradingMode !== 'timed' || phase !== 'quiz' || timeRemaining !== 0) return;
    if (timedAutoSubmitted.current) return;
    timedAutoSubmitted.current = true;
    const currentAns = selected !== null
      ? { selectedIdx: selected, correct: quizQuestions[current]?.shuffledOptions[selected]?.correct === true }
      : { selectedIdx: null, correct: false, incomplete: true };
    const remaining = quizQuestions.slice(current + 1).map(() => ({ selectedIdx: null, correct: false, incomplete: true }));
    const finalAnswers = [...answers, currentAns, ...remaining];
    setAnswers(finalAnswers);
    saveScore(finalAnswers);
    clearSavedQuiz();
    setShowFullReview(true);
    setPhase('results');
  }, [timeRemaining]); // eslint-disable-line react-hooks/exhaustive-deps

  const TOPIC_LABELS = {
    gas_general_licensing:      'General & Licensing',
    gas_pressure_piping:        'Pressure, System Design & Piping',
    gas_joints_supports:        'Joints, Supports & Connections',
    gas_appliances_venting:     'Appliances & Venting',
    gas_installation_emergency: 'Installation, Testing & Emergency',
    gas_propane:                'LP / Propane',
    mill_hydraulics:   'Hydraulics & Fluid Power',
    mill_pneumatics:   'Pneumatics & Compressed Air',
    mill_pumps:        'Pumps & Seals',
    mill_bearings:     'Bearings',
    mill_drives:       'Belts & Drive Systems',
    mill_gears:        'Gears & Speed Reducers',
    mill_couplings:    'Couplings & Clutches',
    mill_shafting:     'Shafting & Alignment',
    mill_conveyors:    'Conveyors',
    mill_machining:    'Machining & Measurement',
    mill_lubrication:  'Lubrication',
    mill_turbines:     'Steam Turbines',
    mill_safety:       'Safety & Fire Prevention',
    mill_math:         'Trade Calculations',
    mill_general:      'General Trade Knowledge',
    quick_oesc:        'OESC Code — Mixed Quiz',
    quick_gas:         'Gas Code — Mixed Quiz',
    smart_missed:      'Smart Quiz — Missed Questions',
    sec_master:        'Master Electrician — Estimating & Management',
    oesc:              'Section 2-36 — General OESC',
    sec4:              'Section 4 — Conductors (Free)',
    sec6:              'Section 6 — Services & Service Equipment',
    sec8:              'Section 8 — Circuit Loading & Demand Factors',
    sec10:             'Section 10 — Grounding & Bonding',
    sec12:             'Section 12 — Wiring Methods',
    sec14:             'Section 14 — Protection and Control',
    sec16:             'Section 16 — Class 1 and Class 2 Circuits',
    sec18:             'Section 18 — Hazardous Locations',
    sec20:             'Section 20 — Flammable Liquids & Hazardous Locations',
    sec22:             'Section 22 — Corrosive Liquids, Vapours & Moisture',
    sec24:             'Section 24 — Patient Care Areas',
    sec26:             'Section 26 — Installation of Electrical Equipment',
    sec28:             'Section 28 — Motors and Generators',
    sec30:             'Section 30 — Installation of Lighting Equipment',
    sec32:             'Section 32 — Fire Alarm Systems & Fire Pumps',
    sec34:             'Section 34 — Signs and Outline Lighting',
    sec36:             'Section 36 — High-Voltage Installations',
    sec38:             'Section 38 — Elevators & Similar Equipment',
    sec40:             'Section 40 — Electric Cranes and Hoists',
    sec42:             'Section 42 — Electric Welders',
    sec44:             'Section 44 — Theatre Installations',
    sec46:             'Section 46 — Emergency Power and Life Safety Systems',
    sec52:             'Section 52 — Diagnostic Imaging Installations',
    sec54:             'Section 54 — Community Antenna Distribution, TV Installations',
    sec56:             'Section 56 — Optical Fiber Cables',
    sec58:             'Section 58 — Passenger Ropeways and Similar Equipment',
    sec60:             'Section 60 — Electrical Communication Systems',
    sec62:             'Section 62 — Fixed Electric Heating Systems',
    sec64:             'Section 64 — Renewable Energy Systems',
    sec66:             'Section 66 — Amusement Parks and Travelling Shows',
    sec68:             'Section 68 — Pools, Tubs, and Spas',
    sec70:             'Section 70 — Factory-Built Relocatable Structures',
    sec72:             'Section 72 — Mobile Home and Recreational Vehicle Parks',
    sec74:             'Section 74 — Airport Installations',
    sec76:             'Section 76 — Temporary Wiring',
    sec78:             'Section 78 — Marine Wharves and Piers',
    sec80:             'Section 80 — Cathodic Protection',
    sec84:             'Section 84 — Interconnection of Power Production Sources',
    sec86:             'Section 86 — Electric Vehicle Charging Systems',
    reg570:            'O. Reg. 570/05 — Licensing',
    elec_act_viii:     'Electricity Act — Part VIII',
    ohsa_duties:       'OHSA — General Duties',
    ohsa_jhsc:         'OHSA — JHSCs & Worker Representation',
    ohsa_whmis:        'OHSA — WHMIS & Hazardous Materials',
    ohsa_violence:     'OHSA — Violence & Harassment',
    ohsa_refuse:       'OHSA — Right to Refuse & Reprisals',
    ohsa_enforce:      'OHSA — Notices, Inspections & Enforcement',
    definitions:       'CEC Section 0 — Definitions & Scope',
    cec_sec2:          'Section 2 — General Rules',
  };

  const sectionLabel = selectedTopic === 'all'
    ? 'All Topics — Mixed Exam'
    : (TOPIC_LABELS[selectedTopic] || selectedTopic);

  const isFreeRun    = hasFullAccess;
  const freeLimit    = selectedTopic === FREE_SECTION ? 20 : selectedTopic === FREE_SECTION_MILL ? 25 : FREE_LIMIT;
  const displayLimit = isFreeRun
    ? quizQuestions.length
    : Math.min(freeLimit, quizQuestions.length);
  const shouldGoToPaywallAtLimit = !isFreeRun && quizQuestions.length > displayLimit;
  const correctSoFar    = answers.filter(a => a.correct).length;
  const incompleteCount = answers.filter(a => a.incomplete).length;
  const wrongCount      = answers.length - correctSoFar - incompleteCount;

  // ── Actions ────────────────────────────────────────────────────────────────
  const startQuiz = (overrideTopic, overrideLicence, overrideCount) => {
    const topic   = overrideTopic   ?? selectedTopic;
    const licence = overrideLicence ?? activeLicence;
    const count   = overrideCount   ?? questionCount;
    if (licence === '433a' || licence === 'gas') return;
    const qs = prepareQuestions(topic, count, licence, hasFullAccess);
    if (!qs.length) return;
    clearSavedQuiz();
    const mode = localStorage.getItem('trp_grading_mode') || 'immediate';
    setGradingMode(mode);
    setShowFullReview(false);
    setShowSubmitConfirm(false);
    setQuestions(qs);
    setCurrent(0);
    setAnswers([]);
    setSelected(null);
    setRevealed(false);
    setVaultQuizMeta(null);
    setPhase('quiz');
  };

  const startVaultQuiz = (licence, trackNumber) => {
    if (!isSubscribed) {
      setModalOpen(true);
      return;
    }
    if (!isQuizVaultTrackUnlocked(vaultProgress, licence, trackNumber)) {
      return;
    }
    const tracks = getQuizVaultTracks(licence);
    const questions = tracks[trackNumber - 1] || [];
    if (!questions.length) return;

    clearSavedQuiz();
    const mode = localStorage.getItem('trp_grading_mode') || 'immediate';
    setGradingMode(mode);
    setShowFullReview(false);
    setShowSubmitConfirm(false);
    setShowExamSim(false);
    setActiveLicence(licence);
    setTopic(`vault_${licence}_${trackNumber}`);
    setCount(String(QUIZ_VAULT_SIZE));
    setQuestions(questions.map(q => withFixedOptions(q, licence)));
    setCurrent(0);
    setAnswers([]);
    setSelected(null);
    setRevealed(false);
    setVaultQuizMeta({ licence, trackNumber });
    setPhase('quiz');
  };

  const openVaultPage = () => {
    navigate(`/vault?licence=${encodeURIComponent(vaultLicence)}`);
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const pendingRaw = localStorage.getItem(QUIZ_VAULT_PENDING_KEY);
    let pending = null;
    try {
      pending = pendingRaw ? JSON.parse(pendingRaw) : null;
    } catch {
      pending = null;
    }

    const quizFromState = location.state?.autoVaultQuiz;
    const quizFromQuery = params.get('vaultQuiz');
    const parsedTrack = Number.parseInt(String(pending?.trackNumber ?? quizFromState ?? quizFromQuery ?? ''), 10);
    if (!Number.isFinite(parsedTrack) || parsedTrack < 1 || parsedTrack > QUIZ_VAULT_TRACKS) return;

    const licenceFromState = location.state?.autoVaultLicence;
    const licenceFromQuery = params.get('vault');
    const licence = pending?.licence || licenceFromState || licenceFromQuery || '309a';
    const isValidLicence = QUIZ_VAULT_LICENCES.some(option => option.value === licence);
    if (!isValidLicence) return;

    startVaultQuiz(licence, parsedTrack);
    if (pendingRaw) localStorage.removeItem(QUIZ_VAULT_PENDING_KEY);
    if (location.search) {
      window.history.replaceState({}, '', location.pathname);
    }
  }, [location.pathname, location.search, location.state, isSubscribed, vaultProgress]); // eslint-disable-line react-hooks/exhaustive-deps

  const replayQuiz = () => {
    if (!quizQuestions.length) return;
    setShowFullReview(false);
    setShowSubmitConfirm(false);
    setCurrent(0);
    setAnswers([]);
    setSelected(null);
    setRevealed(false);
    setPhase('quiz');
  };

  const restartCurrentQuiz = () => {
    if (vaultQuizMeta) {
      startVaultQuiz(vaultQuizMeta.licence, vaultQuizMeta.trackNumber);
      return;
    }
    startQuiz();
  };

  const handleSelect = (idx) => {
    if (revealed) return;
    setSelected(idx);
    if (gradingMode === 'immediate') {
      setRevealed(true);
      if (session) {
        const isCorrect = quizQuestions[current].shuffledOptions[idx]?.correct === true;
        supabase.from('question_answers').insert({
          user_id:  session.user.id,
          qid:      quizQuestions[current].qid,
          topic:    quizQuestions[current].topic,
          licence:  activeLicence,
          correct:  isCorrect,
        }).then(({ error }) => { if (error) console.error('question_answers insert failed:', error); });
      }
    }
  };

  const handleNext = () => {
    if (selected === null) return;
    const isCorrect = quizQuestions[current].shuffledOptions[selected]?.correct === true;
    const newAnswers = [...answers, { selectedIdx: selected, correct: isCorrect }];
    setAnswers(newAnswers);

    const nextIdx = current + 1;

    if (!isFreeRun && nextIdx >= freeLimit && nextIdx < quizQuestions.length) {
      setPhase('paywall');
    } else if (nextIdx >= quizQuestions.length || (!isFreeRun && nextIdx >= freeLimit)) {
      if (gradingMode === 'exam' || gradingMode === 'timed') return;
      saveScore(newAnswers);
      clearSavedQuiz();
      setPhase('results');
    } else {
      setCurrent(nextIdx);
      setSelected(null);
      setRevealed(false);
      resetFlagState();
    }
  };

  const resetFlagState = () => {
    setShowFlagModal(false);
    setFlagReason('');
    setFlagComment('');
    setFlagSubmitted(false);
    setFlagSubmitting(false);
  };

  const handleFlagSubmit = async () => {
    if (!flagReason) return;
    setFlagSubmitting(true);
    await supabase.from('flagged_questions').insert({
      user_id: session?.user?.id || null,
      question_text: currentQ?.question?.slice(0, 500),
      topic: currentQ?.topicLabel,
      reason: flagReason,
      comment: flagComment || null,
    });
    setFlagSubmitting(false);
    setFlagSubmitted(true);
  };

  const handleSubmitExam = () => {
    if (selected === null) return;
    setShowSubmitConfirm(true);
  };

  const confirmSubmitExam = () => {
    const isCorrect = quizQuestions[current].shuffledOptions[selected]?.correct === true;
    const newAnswers = [...answers, { selectedIdx: selected, correct: isCorrect }];
    setAnswers(newAnswers);
    setShowSubmitConfirm(false);
    setShowFullReview(true);
    saveScore(newAnswers);
    clearSavedQuiz();
    if (session) {
      const rows = newAnswers.map((a, i) => ({
        user_id:  session.user.id,
        qid:      quizQuestions[i].qid,
        topic:    quizQuestions[i].topic,
        licence:  activeLicence,
        correct:  a.correct,
      }));
      supabase.from('question_answers').insert(rows)
        .then(({ error }) => { if (error) console.error('question_answers insert failed:', error); });
    }
    setPhase('results');
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 50);
  };

  const saveScore = useCallback(async (ans) => {
    const correct = ans.filter(a => a.correct).length;
    const total   = ans.length;
    const pct     = Math.round((correct / total) * 100);
    if (vaultQuizMeta) {
      const nextProgress = {
        ...vaultProgress,
        [vaultQuizMeta.licence]: {
          ...(vaultProgress[vaultQuizMeta.licence] || {}),
          [vaultQuizMeta.trackNumber]: {
            bestScore: Math.max(correct, Number(vaultProgress?.[vaultQuizMeta.licence]?.[vaultQuizMeta.trackNumber]?.bestScore || 0)),
            bestPct: Math.max(pct, Number(vaultProgress?.[vaultQuizMeta.licence]?.[vaultQuizMeta.trackNumber]?.bestPct || 0)),
            lastScore: correct,
            lastPct: pct,
            completedAt: new Date().toISOString(),
          },
        },
      };
      writeVaultProgress(session?.user?.id || null, nextProgress);
      setVaultProgress(nextProgress);
    }

    if (!session) return;

    const topicName = vaultQuizMeta
      ? `Quiz Vault — ${(LICENCE_LABELS[vaultQuizMeta.licence] || vaultQuizMeta.licence)} — Quiz ${vaultQuizMeta.trackNumber}`
      : selectedTopic === 'all'
        ? 'Mixed — All Topics'
        : selectedTopic === 'smart_missed'
          ? 'Smart Quiz — Missed Questions'
          : selectedTopic === 'timed_sim'
            ? 'Timed Exam Simulation'
            : allQuestions.find(q => q.topic === selectedTopic)?.topicLabel ?? selectedTopic;
    await supabase.from('quiz_scores').insert({
      user_id: session.user.id, topic: topicName,
      score: correct, total, percentage: pct,
    });
  }, [session, selectedTopic, vaultProgress, vaultQuizMeta]);

  const handlePaywallPromo = async () => {
    const code = paywallPromoCode.trim().toUpperCase();
    if (!code) return;
    if (!session) {
      navigate(`/subscribe?code=${encodeURIComponent(code)}`);
      return;
    }
    setPaywallPromoLoading(true);
    setPaywallPromoError('');
    const { data, error } = await supabase.rpc('redeem_promo_code', {
      code_to_redeem: code,
      user_id_to_set: session.user.id,
    });
    if (error || !data?.success) {
      setPaywallPromoError(data?.reason || 'Code not recognised. Check your code and try again.');
      setPaywallPromoLoading(false);
      return;
    }
    window.location.replace('/quiz');
  };

  const formatTime = (secs) => {
    const totalMinutes = Math.max(0, Math.floor(secs / 60));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours > 0) {
      return `${hours}h ${minutes.toString().padStart(2, '0')}m`;
    }
    return `${minutes}m`;
  };

  const formatExamDuration = (minutesTotal) => {
    const hours = Math.floor(minutesTotal / 60);
    const minutes = minutesTotal % 60;
    return hours > 0 ? `${hours}h ${minutes.toString().padStart(2, '0')}m` : `${minutes}m`;
  };

  const startTimedExamSetup = () => {
    const questions = Number.parseInt(examSimQuestionCount, 10);
    const minutes = EXAM_SIM_TIME_BY_QUESTION[examSimQuestionCount] || 48;
    const preset = {
      label: `${questions} Questions · ${formatExamDuration(minutes)}`,
      questions,
      minutes,
    };
    const qs = prepareQuestions('all', questions, examSimLicence, true);
    if (!qs.length) return;
    setActiveLicence(examSimLicence);
    setExamPreset(preset);
    setQuestions(qs);
    setTopic('timed_sim');
    setCurrent(0);
    setAnswers([]);
    setSelected(null);
    setRevealed(false);
    setShowFullReview(false);
    setShowSubmitConfirm(false);
    setShowExamSim(false);
    timedAutoSubmitted.current = false;
    setPhase('timed-confirm');
  };

  const startTimedExam = () => {
    setGradingMode('timed');
    setTimeRemaining(examPreset.minutes * 60);
    setPhase('quiz');
  };

  const resetToSetup = () => {
    clearSavedQuiz();
    setPhase('setup');
    setSelected(null);
    setRevealed(false);
    setCurrent(0);
    setAnswers([]);
    setShowFullReview(false);
    setShowSubmitConfirm(false);
    setActiveTab('quiz');
    setTimeRemaining(0);
    setExamPreset(null);
    setShowExamSim(false);
    setVaultQuizMeta(null);
    const mode = localStorage.getItem('trp_grading_mode') || 'immediate';
    setGradingMode(mode);
    timedAutoSubmitted.current = false;
  };

  const startSmartQuiz = (pool, topicKey) => {
    clearSavedQuiz();
    const qs = shuffle([...pool]).map(withShuffledOptions);
    setShowFullReview(false);
    setShowSubmitConfirm(false);
    setTopic(topicKey);
    setQuestions(qs);
    setCurrent(0);
    setAnswers([]);
    setSelected(null);
    setRevealed(false);
    setActiveTab('quiz');
    setPhase('quiz');
  };

  const startMissedQuiz = async () => {
    if (!session) return;
    setSmartQuizLoading(true);
    setSmartQuizMessage('');
    const pool = await getMissedQuestions(session.user.id, activeLicence);
    setSmartQuizLoading(false);
    if (!pool.length) {
      setSmartQuizMessage('No missed questions yet — keep quizzing!');
      return;
    }
    startSmartQuiz(pool, 'smart_missed');
  };

  const startWeakestTopicQuiz = async () => {
    if (!session) return;
    setSmartQuizLoading(true);
    setSmartQuizMessage('');
    const result = await getWeakestTopic(session.user.id, activeLicence);
    setSmartQuizLoading(false);
    if (!result) {
      setSmartQuizMessage("You're above 70% in all topics — great work!");
      return;
    }
    const pool = allQuestions.filter(
      q => q.topic === result.topic && q.licences.includes(activeLicence)
    );
    startSmartQuiz(pool, result.topic);
  };

  // Called when a Quick Quiz tab is clicked — skips setup, starts immediately
  const startQuickQuiz = (tabId, pool, licence) => {
    if (licence === '433a' || licence === 'gas') return;
    const qs = prepareQuestions(pool, 30, licence, hasFullAccess);
    if (!qs.length) return;
    const mode = localStorage.getItem('trp_grading_mode') || 'immediate';
    clearSavedQuiz();
    setGradingMode(mode);
    setShowFullReview(false);
    setShowSubmitConfirm(false);
    setActiveLicence(licence);
    setTopic(pool);
    setQuestions(qs);
    setCurrent(0);
    setAnswers([]);
    setSelected(null);
    setRevealed(false);
    setPhase('quiz');
    setActiveTab(tabId);
  };

  const handlePrevious = () => {
    if (current === 0) return;
    const prev = answers[answers.length - 1];
    setAnswers(a => a.slice(0, -1));
    setCurrent(c => c - 1);
    setSelected(prev.selectedIdx);
    setRevealed(true);
  };

  const currentQ = quizQuestions[current];
  const resumeTopicLabel = resumeMeta?.topic === 'all'
    ? 'All Topics'
    : (TOPIC_LABELS[resumeMeta?.topic] || resumeMeta?.topic);
  const safeDailyGoalTarget = Math.max(1, dailyGoalTarget || DEFAULT_DAILY_GOAL_TARGET);
  const dailyGoalPct = Math.min(100, Math.round((dailyAnswered / safeDailyGoalTarget) * 100));
  const practiceStreak = Math.max(0, dailyAnswered);

  const formatResultDate = (iso) => {
    if (!iso) return '';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  const formatResultScore = (row) => {
    const score = Number(row?.score) || 0;
    const total = Math.max(Number(row?.total) || 0, 0);
    const pct = Number.isFinite(Number(row?.percentage))
      ? Number(row.percentage)
      : (total > 0 ? Math.round((score / total) * 100) : 0);
    return total > 0 ? `${score}/${total} (${pct}%)` : `${pct}%`;
  };

  const renderSidebarInsights = () => (
    <>
      <div className="sidebar-divider" />

      <div className="sidebar-item" style={{ cursor: 'default', alignItems: 'flex-start', flexDirection: 'column', gap: '0.35rem' }}>
        <div style={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontWeight: 700 }}>Practice Streak</span>
          <span style={{ fontSize: '0.82rem', color: 'var(--gold)' }}>
            {dailyGoalLoading ? 'Loading...' : `🔥 ${practiceStreak}`}
          </span>
        </div>
        <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)' }}>
          {session
            ? (dailyAnswered >= safeDailyGoalTarget
              ? `You are ${Math.max(0, dailyAnswered - safeDailyGoalTarget)} questions ahead of your goal.`
              : `${safeDailyGoalTarget - dailyAnswered} more questions to hit your target.`)
            : 'Sign in to track your daily streak.'}
        </div>
      </div>

      <div className="sidebar-divider" />

      <div className="sidebar-item" style={{ cursor: 'default', alignItems: 'flex-start', flexDirection: 'column', gap: '0.35rem' }}>
        <div style={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontWeight: 700 }}>Daily Goal</span>
          <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
            {dailyGoalLoading ? 'Loading...' : `${dailyAnswered}/${safeDailyGoalTarget}`}
          </span>
        </div>
        <div style={{ width: '100%', height: '8px', borderRadius: '999px', background: 'rgba(255,255,255,0.08)', overflow: 'hidden' }}>
          <div style={{ width: `${dailyGoalPct}%`, height: '100%', background: dailyGoalPct >= 100 ? '#3cb371' : 'var(--gold)' }} />
        </div>
        <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)' }}>
          {session
            ? (dailyGoalPct >= 100 ? 'Goal reached. Great work today.' : `${safeDailyGoalTarget - Math.min(dailyAnswered, safeDailyGoalTarget)} more questions to hit today's goal.`)
            : 'Sign in to track your daily progress.'}
        </div>
      </div>

      <div className="sidebar-divider" />

      <div className="sidebar-item" style={{ cursor: 'default', alignItems: 'flex-start', flexDirection: 'column', gap: '0.35rem' }}>
        <div style={{ width: '100%', fontWeight: 700 }}>Last 5 Results</div>
        {!session && (
          <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)' }}>
            Sign in to see your latest quiz history.
          </div>
        )}
        {session && recentScoresLoading && (
          <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)' }}>Loading recent quizzes...</div>
        )}
        {session && !recentScoresLoading && recentScores.length === 0 && (
          <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)' }}>No quiz results yet. Start your first quiz.</div>
        )}
        {session && !recentScoresLoading && recentScores.length > 0 && recentScores.map((row, idx) => (
          <div
            key={`${row.created_at || 'row'}-${idx}`}
            style={{
              width: '100%',
              fontSize: '0.9rem',
              color: darkMode ? '#eef3ff' : 'var(--navy)',
              display: 'grid',
              gridTemplateColumns: '1fr auto',
              gap: '0.38rem',
              padding: '0.45rem 0.55rem',
              borderRadius: '8px',
              background: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(12,31,63,0.08)',
            }}
          >
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text-muted)', fontWeight: 700, fontSize: '0.85rem' }}>{row.topic || 'Quiz'}</span>
            <span
              style={{
                color: 'var(--gold)',
                background: 'rgba(201,168,76,0.15)',
                border: 'none',
                fontWeight: 800,
                padding: '0.1rem 0.4rem',
                borderRadius: '6px',
              }}
            >
              {formatResultScore(row)}
            </span>
            <span style={{ gridColumn: '1 / span 2', fontSize: '0.85rem', color: 'var(--text-muted)', fontWeight: 700 }}>{formatResultDate(row.created_at)}</span>
          </div>
        ))}
      </div>
    </>
  );

  const ELEC_TOPICS = [
    { label: 'Estimating & Management',                  topic: 'sec_master',    licences: ['master'] },
    { label: 'Section 2 — General Rules',                topic: 'cec_sec2',      licences: ['309a', '309c', '442a'] },
    { label: 'Section 2-36 — General OESC',              topic: 'oesc',          licences: ['master'] },
    { label: 'Section 4 — Conductors',                   topic: 'sec4',          licences: ['master', '309a', '309c', '442a'], free: true },
    { label: 'Section 6 — Services & Equipment',         topic: 'sec6',          licences: ['master', '309a', '309c', '442a'] },
    { label: 'Section 8 — Circuit Loading',              topic: 'sec8',          licences: ['master', '309a', '309c', '442a'] },
    { label: 'Section 10 — Grounding & Bonding',         topic: 'sec10',         licences: ['master', '309a', '442a'] },
    { label: 'Section 12 — Wiring Methods',              topic: 'sec12',         licences: ['master', '309a', '442a'] },
    { label: 'Section 14 — Protection & Control',        topic: 'sec14',         licences: ['master', '309a', '442a'] },
    { label: 'Section 16 — Class 1 & 2 Circuits',       topic: 'sec16',         licences: ['master', '309a', '442a'] },
    { label: 'Section 18 — Hazardous Locations',         topic: 'sec18',         licences: ['master', '309a', '442a'] },
    { label: 'Section 20 — Flammable Liquids & Hazardous Locations', topic: 'sec20', licences: ['master', '309a', '442a'] },
    { label: 'Section 22 — Corrosive Liquids, Vapours & Moisture',  topic: 'sec22', licences: ['master', '309a', '442a'] },
    { label: 'Section 24 — Patient Care Areas',          topic: 'sec24',         licences: ['master', '309a', '442a'] },
    { label: 'Section 26 — Installation of Electrical Equipment', topic: 'sec26', licences: ['master', '309a', '442a'] },
    { label: 'Section 28 — Motors & Generators',         topic: 'sec28',         licences: ['master', '309a', '442a'] },
    { label: 'Section 30 — Installation of Lighting Equipment', topic: 'sec30', licences: ['master', '309a', '442a'] },
    { label: 'Section 32 — Fire Alarm Systems & Fire Pumps', topic: 'sec32',       licences: ['master', '309a', '442a'] },
    { label: 'Section 34 — Signs and Outline Lighting',      topic: 'sec34',       licences: ['master', '309a', '442a'] },
    { label: 'Section 36 — High-Voltage Installations',  topic: 'sec36',         licences: ['master', '442a'] },
    { label: 'Section 38 — Elevators & Similar Equipment',  topic: 'sec38',       licences: ['master', '309a', '442a'] },
    { label: 'Section 40 — Electric Cranes and Hoists',  topic: 'sec40',         licences: ['442a'] },
    { label: 'Section 42 — Electric Welders',            topic: 'sec42',         licences: ['442a'] },
    { label: 'Section 44 — Theatre Installations',       topic: 'sec44',         licences: ['442a'] },
    { label: 'Section 46 — Emergency Power and Life Safety Systems', topic: 'sec46', licences: ['309a', '442a'] },
    { label: 'Section 52 — Diagnostic Imaging Installations', topic: 'sec52', licences: ['442a'] },
    { label: 'Section 54 — Community Antenna Distribution, TV Installations', topic: 'sec54', licences: ['309a', '442a'] },
    { label: 'Section 56 — Optical Fiber Cables',        topic: 'sec56',         licences: ['309a', '442a'] },
    { label: 'Section 58 — Passenger Ropeways and Similar Equipment', topic: 'sec58', licences: ['442a'] },
    { label: 'Section 60 — Electrical Communication Systems', topic: 'sec60', licences: ['309a', '442a'] },
    { label: 'Section 62 — Fixed Electric Heating Systems', topic: 'sec62', licences: ['309a', '442a'] },
    { label: 'Section 64 — Renewable Energy Systems', topic: 'sec64', licences: ['309a', '442a'] },
    { label: 'Section 66 — Amusement Parks and Travelling Shows', topic: 'sec66', licences: ['442a'] },
    { label: 'Section 68 — Pools, Tubs, and Spas', topic: 'sec68', licences: ['309a', '442a'] },
    { label: 'Section 70 — Factory-Built Relocatable Structures', topic: 'sec70', licences: ['442a'] },
    { label: 'Section 72 — Mobile Home and Recreational Vehicle Parks', topic: 'sec72', licences: ['442a'] },
    { label: 'Section 74 — Airport Installations', topic: 'sec74', licences: ['442a'] },
    { label: 'Section 76 — Temporary Wiring', topic: 'sec76', licences: ['309a', '442a'] },
    { label: 'Section 78 — Marine Wharves and Piers', topic: 'sec78', licences: ['309a', '442a'] },
    { label: 'Section 80 — Cathodic Protection', topic: 'sec80', licences: ['442a'] },
    { label: 'Section 84 — Interconnection of Power Production Sources', topic: 'sec84', licences: ['309a', '442a'] },
    { label: 'Section 86 — Electric Vehicle Charging Systems', topic: 'sec86', licences: ['309a', '442a'] },
    { label: 'O. Reg. 570/05 — Licensing',               topic: 'reg570',        licences: ['master'] },
    { label: 'Electricity Act — Part VIII',               topic: 'elec_act_viii', licences: ['master'] },
    { label: 'OHSA — General Duties',                     topic: 'ohsa_duties',   licences: ['master'] },
    { label: 'OHSA — JHSCs & Worker Representation',     topic: 'ohsa_jhsc',     licences: ['master'] },
    { label: 'OHSA — WHMIS & Hazardous Materials',       topic: 'ohsa_whmis',    licences: ['master'] },
    { label: 'OHSA — Violence & Harassment',              topic: 'ohsa_violence', licences: ['master'] },
    { label: 'OHSA — Right to Refuse & Reprisals',       topic: 'ohsa_refuse',   licences: ['master'] },
    { label: 'OHSA — Notices, Inspections & Enforcement', topic: 'ohsa_enforce', licences: ['master'] },
    { label: 'Section 0 — Definitions & Scope',           topic: 'definitions',   licences: ['309a', '309c', '442a'] },
  ];

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      {/* ── HEADER ────────────────────────────────────────────────────────── */}
      <header>
        <div className="logo" onClick={() => { if (session) { navigate('/quiz'); resetToSetup(); return; } navigate('/home'); resetToSetup(); }}>
          <div className="logo-icon"><img src={logoIcon} alt="" /></div>
          <div className="logo-text">TradeReady<span>Pro</span></div>
        </div>
        <div className="header-right">
          {session && <Link to="/quiz" className="dark-toggle" onClick={resetToSetup}>Home</Link>}
          {session && <Link to="/dashboard" className="dark-toggle">My Scores</Link>}
          {session && <button className="dark-toggle" onClick={handleSignOut}>Sign Out</button>}
          {!session && <Link to="/login" className="dark-toggle">Login</Link>}
          {!session && <Link to="/home" className="dark-toggle" onClick={resetToSetup}>Home</Link>}
          {!session && <button className="btn-subscribe" onClick={() => navigate('/subscribe')}>Subscribe</button>}
          <Link to="/settings" className="dark-toggle" title="Settings">⚙️</Link>
        </div>
      </header>

      {/* ── MOBILE DRAWER ─────────────────────────────────────────────────── */}
      {drawerOpen && <div className="drawer-overlay" onClick={closeDrawer} />}
      <nav className={`drawer${drawerOpen ? ' drawer-open' : ''}`}>
        <div className="drawer-header">
          <span className="drawer-title">Select Trade</span>
          <button className="drawer-close" onClick={closeDrawer}>✕</button>
        </div>

        <div className="sidebar-section-label">🇨🇦 Canadian Trades</div>

        {/* Electrician — drawer */}
        <button className={`sidebar-item${elecOpen ? ' sidebar-item-open' : ''}`} style={{ fontWeight: 600 }} onClick={() => setElecOpen(o => !o)}>
          <span className="trade-icon">⚡</span>
          <span>Electrician</span>
          <span className="sidebar-arrow">{elecOpen ? '▾' : '▸'}</span>
        </button>
        {elecOpen && (
          <div className="sidebar-group-content">
            {[
              { key: 'master', label: 'Master Electrician' },
              { key: '442a',   label: '442A Industrial Electrician' },
              { key: '309a',   label: '309A C&M Electrician' },
              { key: '309c',   label: '309C Domestic & Rural' },
            ].map(({ key, label }) => (
              <div key={key}>
                <button
                  className={`sidebar-item sidebar-sub${activeLicence === key ? ' active' : ''}`}
                  onClick={() => { setActiveLicence(key); setActiveTab('quiz'); setTopic('all'); resetToSetup(); setOpenElecSub(o => o === key ? null : key); }}
                >
                  <span style={{ marginLeft: '1.5rem' }}>{label}</span>
                  <span className="sidebar-badge">Active</span>
                  <span className="sidebar-arrow" style={{ marginLeft: '0.4rem' }}>{openElecSub === key ? '▾' : '▸'}</span>
                </button>
                {openElecSub === key && (
                  <div>
                    {ELEC_TOPICS.filter(t => t.licences.includes(key)).map(({ label: tLabel, topic, free }) => (
                      <button
                        key={topic}
                        className={`sidebar-item sidebar-sub${activeLicence === key && selectedTopic === topic ? ' active' : ''}`}
                        onClick={() => { setActiveLicence(key); setActiveTab('quiz'); setTopic(topic); resetToSetup(); closeDrawer(); }}
                      >
                        <span style={{ marginLeft: '3rem' }}>{tLabel}</span>
                        <span className={`sidebar-badge${free && !hasFullAccess ? ' sidebar-badge-free' : ''}`}>{free && !hasFullAccess ? 'FREE' : 'Active'}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="sidebar-divider" />
        <div className="sidebar-section-label" style={{ marginTop: '1.2rem' }}>⚡ Safety Training</div>
        <button
          className={`sidebar-item${arcFlashOpen ? ' sidebar-item-open' : ''}`}
          style={{ fontWeight: 600 }}
          onClick={() => {
            if (practiceArcFlashLocked) {
              openArcFlashCourse();
              return;
            }
            setArcFlashOpen(o => !o);
          }}
        >
          <span className="trade-icon">⚡</span>
          <span>Arc Flash &amp; Electrical Safety Awareness</span>
          <span className="sidebar-arrow">{arcFlashOpen ? '▾' : '▸'}</span>
        </button>
        {arcFlashOpen && (
          <div className="sidebar-group-content">
            {ARC_FLASH_MODULES.map((module) => (
              <button
                key={module.key}
                className={`sidebar-item sidebar-sub${module.locked ? ' active' : ''}`}
                disabled={module.locked}
                onClick={() => { closeDrawer(); openArcFlashCourse(module.key === 'arc-final' ? 'exam' : module.key.replace('arc-', '')); }}
                style={module.locked ? { opacity: 0.7, cursor: 'not-allowed', marginLeft: '0.5rem' } : { marginLeft: '0.5rem' }}
              >
                <span style={{ marginLeft: '3rem' }}>{module.title}</span>
                <span className="sidebar-badge">{module.locked ? 'Locked' : 'Open'}</span>
              </button>
            ))}
          </div>
        )}

        <div className="sidebar-divider" />
        <div className="sidebar-section-label" style={{ marginTop: '1.2rem' }}>Subscriber Features</div>

        {renderSidebarInsights()}

        <button
          className="sidebar-item"
          onClick={() => { resumeSavedQuiz(); closeDrawer(); }}
          disabled={!resumeMeta}
          style={!resumeMeta ? { opacity: 0.55, cursor: 'not-allowed' } : undefined}
          title={resumeMeta ? 'Resume your most recent in-progress quiz' : 'No in-progress quiz saved yet'}
        >
          <span className="trade-icon">⏯️</span>
          <span>{resumeMeta ? `Resume Last Quiz (${resumeMeta.answered}/${resumeMeta.total})` : 'Resume Last Quiz'}</span>
        </button>
        {resumeMeta && (
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', padding: '0.1rem 0.9rem 0.6rem 2.6rem' }}>
            {LICENCE_LABELS[resumeMeta.licence] || 'Quiz'} • {resumeTopicLabel}
          </div>
        )}
        {session && (
          <Link to="/dashboard" className="sidebar-item" onClick={closeDrawer}>
            <span className="trade-icon">📊</span>
            <span>My Scores</span>
          </Link>
        )}
        <Link to="/settings" className="sidebar-item" onClick={closeDrawer}>
          <span className="trade-icon">⚙️</span>
          <span>Settings</span>
        </Link>
      </nav>

      <div className="app-body">

        {/* ── SIDEBAR ───────────────────────────────────────────────────────── */}
        <aside className="sidebar">
          <div className="sidebar-section-label">🇨🇦 Canadian Trades</div>

          {/* Electrician — desktop sidebar */}
          <button className={`sidebar-item${elecOpen ? ' sidebar-item-open' : ''}`} style={{ fontWeight: 600 }} onClick={() => setElecOpen(o => !o)}>
            <span className="trade-icon">⚡</span>
            <span>Electrician</span>
            <span className="sidebar-arrow">{elecOpen ? '▾' : '▸'}</span>
          </button>
          {elecOpen && (
            <div className="sidebar-group-content">
              {[
                { key: 'master', label: 'Master Electrician' },
                { key: '442a',   label: '442A Industrial Electrician' },
                { key: '309a',   label: '309A C&M Electrician' },
                { key: '309c',   label: '309C Domestic & Rural' },
              ].map(({ key, label }) => (
                <div key={key}>
                  <button
                    className={`sidebar-item sidebar-sub${activeLicence === key ? ' active' : ''}`}
                    onClick={() => { setActiveLicence(key); setActiveTab('quiz'); setTopic('all'); resetToSetup(); setOpenElecSub(o => o === key ? null : key); }}
                  >
                    <span style={{ marginLeft: '1.5rem' }}>{label}</span>
                    <span className="sidebar-badge">Active</span>
                    <span className="sidebar-arrow" style={{ marginLeft: '0.4rem' }}>{openElecSub === key ? '▾' : '▸'}</span>
                  </button>
                  {openElecSub === key && (
                    <div>
                      {ELEC_TOPICS.filter(t => t.licences.includes(key)).map(({ label: tLabel, topic, free }) => (
                        <button
                          key={topic}
                          className={`sidebar-item sidebar-sub${activeLicence === key && selectedTopic === topic ? ' active' : ''}`}
                          onClick={() => { setActiveLicence(key); setActiveTab('quiz'); setTopic(topic); resetToSetup(); }}
                        >
                          <span style={{ marginLeft: '3rem' }}>{tLabel}</span>
                          <span className={`sidebar-badge${free && !hasFullAccess ? ' sidebar-badge-free' : ''}`}>{free && !hasFullAccess ? 'FREE' : 'Active'}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="sidebar-divider" />
          <div className="sidebar-section-label" style={{ marginTop: '1.2rem' }}>⚡ Safety Training</div>
          <button
            className={`sidebar-item${arcFlashOpen ? ' sidebar-item-open' : ''}`}
            style={{ fontWeight: 600 }}
            onClick={() => {
              if (practiceArcFlashLocked) {
                openArcFlashCourse();
                return;
              }
              setArcFlashOpen(o => !o);
            }}
          >
            <span className="trade-icon">⚡</span>
            <span>Arc Flash &amp; Electrical Safety Awareness</span>
            <span className="sidebar-arrow">{arcFlashOpen ? '▾' : '▸'}</span>
          </button>
          {arcFlashOpen && (
            <div className="sidebar-group-content">
              {ARC_FLASH_MODULES.map((module) => (
                <button
                  key={module.key}
                  className={`sidebar-item sidebar-sub${module.locked ? ' active' : ''}`}
                  disabled={module.locked}
                  onClick={() => openArcFlashCourse(module.key === 'arc-final' ? 'exam' : module.key.replace('arc-', ''))}
                  style={module.locked ? { opacity: 0.7, cursor: 'not-allowed', marginLeft: '0.5rem' } : { marginLeft: '0.5rem' }}
                >
                  <span style={{ marginLeft: '3rem' }}>{module.title}</span>
                  <span className="sidebar-badge">{module.locked ? 'Locked' : 'Open'}</span>
                </button>
              ))}
            </div>
          )}

          <div className="sidebar-divider" />
          <div className="sidebar-section-label" style={{ marginTop: '1.2rem' }}>Subscriber Features</div>

          <button
            className="sidebar-item"
            onClick={resumeSavedQuiz}
            disabled={!resumeMeta}
            style={!resumeMeta ? { opacity: 0.55, cursor: 'not-allowed' } : undefined}
            title={resumeMeta ? 'Resume your most recent in-progress quiz' : 'No in-progress quiz saved yet'}
          >
            <span className="trade-icon">⏯️</span>
            <span>{resumeMeta ? `Resume Last Quiz (${resumeMeta.answered}/${resumeMeta.total})` : 'Resume Last Quiz'}</span>
          </button>
          {resumeMeta && (
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', padding: '0.1rem 0.9rem 0.6rem 2.6rem' }}>
              {LICENCE_LABELS[resumeMeta.licence] || 'Quiz'} • {resumeTopicLabel}
            </div>
          )}

          {renderSidebarInsights()}

        </aside>

        {/* ── MAIN CONTENT ──────────────────────────────────────────────────── */}
        <div className="main-content">

          {arcFlashView ? (
            <ArcFlash
              session={session}
              hasAccess={hasFullAccess || isSubscribed}
              hasFullAccess={hasFullAccess}
              isSubscribed={isSubscribed}
              loading={false}
              subscribePath="/subscribe"
            />
          ) : (
            <>
              {/* ── QUIZ / QUICK QUIZ CONTENT ─────────────────────────────────── */}
              {(activeTab === 'quiz' || activeTab === 'oesc' || activeTab === 'ohsaquiz' || activeTab === 'regsquiz') && (
            <>
              {/* SETUP */}
              {phase === 'setup' && (
                <>
                  <div className="hero">
                    <div className="hero-tag">🇨🇦 {LICENCE_LABELS[activeLicence]} Exam Prep</div>
                    <h1>Pass your exam. Get your ticket. <span>First try.</span> TradeReady Pro</h1>
                    {isMillwright ? (
                      <p>Practice with real exam questions covering hydraulics, pneumatics, bearings, drives, alignment, machining, and more. Every answer explained. 433A referenced.</p>
                    ) : isGasFitter ? (
                      <p>Practice with real exam questions drawn from B149.1, the TSSA Act, and Ontario gas technician licensing exams. Every answer explained. Every clause referenced.</p>
                    ) : (
                      <>
                        <p style={{ marginBottom: '0.25rem', fontWeight: 'normal' }}>Get exam-ready with simulated questions from the OESC (Master Electrician), CEC-based section prep (309A, 442A, and 309C), OHSA Green Book, and Acts &amp; Regulations.</p>
                        <p style={{ marginBottom: '0.25rem', fontWeight: 'normal' }}>Subscribers unlock progress tracking — watch your scores climb and walk into your exam knowing you're ready.</p>
                      </>
                    )}
                    <div className="hero-stats">
                      <div className="stat">
                        <div className="stat-num">2,093+</div>
                        <div className="stat-label">Practice Questions</div>
                      </div>
                      <div className="stat">
                        <div className="stat-num">{isMillwright ? '433A' : isGasFitter ? 'B149.1' : 'CEC • OESC'}</div>
                        <div className="stat-label">Code Referenced</div>
                      </div>
                      <div className="stat">
                        <div className="stat-num">{isGasFitter ? 'TSSA' : 'OHSA'}</div>
                        <div className="stat-label">{isGasFitter ? 'Act Aligned' : 'Green Book Aligned'}</div>
                      </div>
                    </div>
                  </div>

                  <div className="quiz-setup-card section-shell">
                    <div className="section-header">
                      <div className="section-copy">
                        <h3 className="section-title">Choose your quiz</h3>
                      </div>
                    </div>
                    <p className="section-subtitle">Pick your trade, section, and question count.</p>

                    <div className="section-indicator" style={{ marginTop: '1rem' }}>
                      <div className="section-indicator-trade">🇨🇦 {LICENCE_LABELS[activeLicence]}</div>
                      <div className={`section-indicator-name${selectedTopic === 'all' ? ' section-indicator-all' : ''}`}>
                        {selectedTopic === 'all' ? 'All Topics — Full Question Bank' : sectionLabel}
                      </div>
                    </div>

                    <div className="quiz-controls">
                    <select
                      className="select-styled"
                      value={QUIZ_LICENCE_OPTIONS.some(o => o.value === activeLicence) ? activeLicence : '309a'}
                      onChange={e => {
                        setActiveLicence(e.target.value);
                        setTopic('all');
                      }}
                    >
                      {QUIZ_LICENCE_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>

                    <select
                      className="select-styled"
                      value={selectedTopic}
                      onChange={e => setTopic(e.target.value)}
                    >
                      <option value="all">All Sections (Mixed)</option>
                      {isMillwright ? (
                        <optgroup label="── 433A Millwright Topics ──">
                          <option value="mill_hydraulics">Hydraulics &amp; Fluid Power</option>
                          <option value="mill_pneumatics">Pneumatics &amp; Compressed Air</option>
                          <option value="mill_pumps">Pumps &amp; Seals</option>
                          <option value="mill_bearings">Bearings</option>
                          <option value="mill_drives">Belts &amp; Drive Systems</option>
                          <option value="mill_gears">Gears &amp; Speed Reducers</option>
                          <option value="mill_couplings">Couplings &amp; Clutches</option>
                          <option value="mill_shafting">Shafting &amp; Alignment</option>
                          <option value="mill_conveyors">Conveyors</option>
                          <option value="mill_machining">Machining &amp; Measurement</option>
                          <option value="mill_lubrication">Lubrication</option>
                          <option value="mill_turbines">Steam Turbines</option>
                          <option value="mill_safety">Safety &amp; Fire Prevention</option>
                          <option value="mill_math">Trade Calculations</option>
                          <option value="mill_general">General Trade Knowledge</option>
                        </optgroup>
                      ) : isGasFitter ? (
                        <optgroup label="── B149.1 Gas Fitter Topics ──">
                          <option value="gas_general_licensing">Quiz 1 — General &amp; Licensing</option>
                          <option value="gas_pressure_piping">Quiz 2 — Pressure &amp; Piping</option>
                          <option value="gas_joints_supports">Quiz 3 — Joints &amp; Supports</option>
                          <option value="gas_appliances_venting">Quiz 4 — Appliances &amp; Venting</option>
                          <option value="gas_installation_emergency">Quiz 5 — Installation &amp; Emergency</option>
                          <option value="gas_propane">Quiz 6 — LP / Propane</option>
                        </optgroup>
                      ) : (() => {
                        const avail = ELEC_TOPICS.filter(t => t.licences.includes(activeLicence));
                        const mgmt  = avail.filter(t => t.topic === 'sec_master');
                        const oesc  = avail.filter(t => t.topic === 'oesc' || (t.topic.startsWith('sec') && t.topic !== 'sec_master'));
                        const cec   = avail.filter(t => t.topic === 'definitions' || t.topic.startsWith('cec_'));
                        const regs  = avail.filter(t => t.topic === 'reg570' || t.topic === 'elec_act_viii');
                        const ohsa  = avail.filter(t => t.topic.startsWith('ohsa_'));
                        const codeSectionsLabel = activeLicence === 'master'
                          ? '── OESC Code Sections ──'
                          : '── CEC Code Sections ──';
                        const mkOpt = ({ label, topic, free }) => (
                          <option key={topic} value={topic}>{label}{free ? ' (Free)' : ''}</option>
                        );
                        return (
                          <>
                            {mgmt.length > 0 && <optgroup label="── Master Electrician ──">{mgmt.map(mkOpt)}</optgroup>}
                            {oesc.length > 0 && <optgroup label={codeSectionsLabel}>{oesc.map(mkOpt)}</optgroup>}
                            {cec.length > 0 && <optgroup label="── CEC Code Sections ──">{cec.map(mkOpt)}</optgroup>}
                            {regs.length > 0 && <optgroup label="── Regulatory ──">{regs.map(mkOpt)}</optgroup>}
                            {ohsa.length > 0 && <optgroup label="── OHSA ──">{ohsa.map(mkOpt)}</optgroup>}
                          </>
                        );
                      })()}
                    </select>

                    <select
                      className="select-styled select-styled-count"
                      value={questionCount}
                      onChange={e => setCount(e.target.value)}
                    >
                      <option value="5">5 Questions</option>
                      <option value="10">10 Questions</option>
                      <option value="20">20 Questions</option>
                      <option value="all">Full Bank</option>
                    </select>

                    <button className="btn-start" onClick={() => startQuiz()}>▶ Start Quiz</button>
                    </div>

                    <div className="quiz-mode-row">
                      <div className="quiz-mode-selector" aria-label="Quiz grading mode selector">
                        <span className="quiz-mode-label">Grading Mode</span>
                        <div className="quiz-mode-options" role="group" aria-label="Select grading mode">
                          <button
                            type="button"
                            className={`quiz-mode-option${selectedGradingMode === 'immediate' ? ' active' : ''}`}
                            onClick={() => handleGradingMode('immediate')}
                            aria-pressed={selectedGradingMode === 'immediate'}
                          >
                            Immediate
                          </button>
                          <button
                            type="button"
                            className={`quiz-mode-option${selectedGradingMode === 'exam' ? ' active' : ''}`}
                            onClick={() => handleGradingMode('exam')}
                            aria-pressed={selectedGradingMode === 'exam'}
                          >
                            Exam
                          </button>
                        </div>
                      </div>
                      <p className="quiz-mode-help">
                        Immediate shows feedback after each question. Exam saves feedback for the final review.
                      </p>
                    </div>
                  </div>

                  <div className="section-shell smart-quiz-shell">
                    <div className="section-header">
                      <div className="section-copy">
                        <h3 className="section-title">Smart Quiz</h3>
                      </div>
                    </div>
                    <p className="section-subtitle">Target missed questions and weak topics.</p>

                    <div className="smart-quiz-bar" style={{ marginTop: '1rem' }}>
                    <button
                      className={`btn-smart btn-smart-navy${!hasFullAccess ? ' btn-smart-locked' : ''}`}
                      onClick={hasFullAccess ? () => {
                        setShowExamSim(false);
                        startMissedQuiz();
                      } : () => setModalOpen(true)}
                      disabled={hasFullAccess && smartQuizLoading}
                    >
                      {hasFullAccess ? '🎯' : '🔒'} Missed Questions
                    </button>
                    <button
                      className={`btn-smart btn-smart-navy${!hasFullAccess ? ' btn-smart-locked' : ''}`}
                      onClick={hasFullAccess ? () => {
                        setShowExamSim(false);
                        startWeakestTopicQuiz();
                      } : () => setModalOpen(true)}
                      disabled={hasFullAccess && smartQuizLoading}
                    >
                      {hasFullAccess ? '📉' : '🔒'} Weakest Topic
                    </button>
                    <button
                      className={`btn-smart btn-smart-navy${!hasFullAccess ? ' btn-smart-locked' : ''}`}
                      onClick={hasFullAccess ? () => setShowExamSim(s => !s) : () => setModalOpen(true)}
                    >
                      {hasFullAccess ? '⏱️' : '🔒'} Exam Simulator
                    </button>
                    {hasFullAccess && showExamSim && (
                      <div className="exam-sim-controls">
                        <div className="exam-sim-time-lock">
                          Timed pacing: {formatExamDuration(EXAM_SIM_TIME_BY_QUESTION[examSimQuestionCount] || 48)}
                        </div>
                        <select
                          className="select-styled exam-sim-select"
                          value={examSimLicence}
                          onChange={e => setExamSimLicence(e.target.value)}
                          aria-label="Exam simulator licence"
                        >
                          {QUIZ_LICENCE_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                        <select
                          className="select-styled exam-sim-select exam-sim-select-compact"
                          value={examSimQuestionCount}
                          onChange={e => setExamSimQuestionCount(e.target.value)}
                          aria-label="Exam simulator question count"
                        >
                          {EXAM_SIM_QUESTION_OPTIONS.map(count => (
                            <option key={count} value={String(count)}>{count === 100 ? '100 Questions (Full Exam)' : `${count} Questions`}</option>
                          ))}
                        </select>
                        <button className="btn-exam-start" onClick={startTimedExamSetup}>
                          Start Timed Sim
                        </button>
                      </div>
                    )}
                    {hasFullAccess && smartQuizLoading && <span className="smart-quiz-bar-status">Loading…</span>}
                    {hasFullAccess && !smartQuizLoading && smartQuizMessage && (
                      <span className="smart-quiz-bar-status">{smartQuizMessage}</span>
                    )}
                    {!hasFullAccess && (
                      <span className="smart-quiz-bar-upsell">Subscriber feature — Missed questions, weak spot targeting, and timed exam simulation.</span>
                    )}
                    </div>
                  </div>

                  <div className="quiz-vault-card section-shell">
                    <div className="section-header">
                      <div className="section-copy">
                        <h3 className="section-title">Quiz Vault</h3>
                      </div>
                    </div>
                    <p className="section-subtitle">Pass each quiz with 70% to unlock the next one. Twelve fixed 25-question quizzes per licence.</p>

                    <div className="quiz-vault-controls-row">
                      <div className="quiz-vault-licence-tabs">
                        {QUIZ_VAULT_LICENCES.map(option => (
                          <button
                            key={option.value}
                            type="button"
                            className={`quiz-vault-licence-btn btn-smart btn-smart-navy${vaultLicence === option.value ? ' active' : ''}${!isSubscribed ? ' btn-smart-locked locked' : ''}`}
                            onClick={() => setVaultLicence(option.value)}
                          >
                            {!isSubscribed ? '🔒 ' : ''}{option.label}
                          </button>
                        ))}
                      </div>
                      <span className="quiz-vault-flow-arrow" aria-hidden="true">→</span>
                      <button className={`btn-open-vault btn-smart btn-open-vault-action${!isSubscribed ? ' btn-smart-locked' : ''}`} onClick={openVaultPage}>
                        {!isSubscribed ? '🔒 ' : ''}OPEN VAULT
                      </button>
                    </div>
                  </div>

                  <div className="content-tabs" style={{ marginTop: '0.25rem' }}>
                    <div className="ctab ctab-label">Quick Safety Quizzes</div>
                    {[
                      { id: 'oesc',         label: '⚡ OESC Code',       pool: 'quick_oesc',   licence: 'master' },
                      { id: 'ohsaquiz',     label: '🦺 OHSA Green Book', pool: 'quick_ohsa',   licence: 'master' },
                      { id: 'regsquiz',     label: '📋 Acts & Regs',     pool: 'quick_regs',   licence: 'master' },
                    ].map(t => (
                      <div
                        key={t.id}
                        className={`ctab${activeTab === t.id ? ' active' : ''}`}
                        onClick={() => startQuickQuiz(t.id, t.pool, t.licence)}
                      >
                        {t.label}
                      </div>
                    ))}
                  </div>

                  {!hasFullAccess && (
                    <>
                      <div
                        className="free-callout free-callout-clickable"
                        onClick={() => { setActiveLicence('master'); setTopic(FREE_SECTION); startQuiz(FREE_SECTION, 'master', 20); }}
                        role="button"
                        tabIndex={0}
                        onKeyDown={e => e.key === 'Enter' && startQuiz(FREE_SECTION, 'master', 20)}
                      >
                        <span className="free-callout-badge">FREE FOR ELECTRICIANS</span>
                        Try a free 20-question sample from Section 4 — Conductors. Other sections include a 3-question preview.
                      </div>
                      <div
                        className="free-callout free-callout-clickable"
                        style={{ marginTop: '0.5rem' }}
                        onClick={() => { setActiveLicence('433a'); setTopic(FREE_SECTION_MILL); startQuiz(FREE_SECTION_MILL, '433a', 25); }}
                        role="button"
                        tabIndex={0}
                        onKeyDown={e => e.key === 'Enter' && startQuiz(FREE_SECTION_MILL, '433a', 25)}
                      >
                        <span className="free-callout-badge">FREE FOR MILLWRIGHTS</span>
                        Try a free 25-question sample from Pumps &amp; Seals. Other sections include a 3-question preview.
                      </div>
                    </>
                  )}
                </>
              )}

              {/* QUIZ */}
              {phase === 'quiz' && currentQ && (
                <>
                  {gradingMode === 'timed' && (
                    <div style={{
                      background: timeRemaining <= 120 ? 'rgba(224,82,82,0.12)' : 'rgba(201,168,76,0.1)',
                      border: `2px solid ${timeRemaining <= 120 ? 'rgba(224,82,82,0.4)' : 'rgba(201,168,76,0.3)'}`,
                      borderRadius: 'var(--radius)', padding: '1rem 1.5rem',
                      marginBottom: '1rem', textAlign: 'center',
                      boxShadow: 'var(--shadow)',
                      transition: 'background 0.5s, border-color 0.5s',
                    }}>
                      <div style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: timeRemaining <= 120 ? '#e05252' : 'var(--gold)', marginBottom: '0.2rem', transition: 'color 0.5s' }}>
                        Time Remaining
                      </div>
                      <div style={{
                        fontFamily: "'Courier New', Courier, monospace",
                        fontSize: '1.5rem', fontWeight: 700, lineHeight: 1,
                        color: timeRemaining <= 120 ? '#e05252' : timeRemaining <= 300 ? 'var(--gold-light)' : 'var(--gold)',
                        letterSpacing: '0.06em',
                        transition: 'color 0.5s',
                      }}>
                        {formatTime(timeRemaining)}
                      </div>
                    </div>
                  )}
                  <div className="progress-bar-wrap">
                    <div className="progress-track">
                      <div
                        className="progress-fill"
                        style={{ width: `${(answers.length / displayLimit) * 100}%` }}
                      />
                    </div>
                    <div className="progress-label">Question {current + 1} of {displayLimit}</div>
                    {gradingMode === 'immediate' && (
                      <div className="score-pill">Score: {correctSoFar}/{answers.length}</div>
                    )}
                    {gradingMode === 'exam' && (
                      <div className="score-pill" style={{ background: 'rgba(201,168,76,0.1)', color: 'var(--gold)' }}>Exam Mode</div>
                    )}
                    {gradingMode === 'timed' && (
                      <div className="score-pill" style={{ background: 'rgba(201,168,76,0.1)', color: 'var(--gold)' }}>Timed Exam</div>
                    )}
                  </div>

                  <div className="question-card">
                    <div className="q-meta">
                      <span className="q-num">Q{current + 1}</span>
                      <span className="q-topic">{currentQ.topicLabel}</span>
                    </div>
                    <div className="q-text">{currentQ.question}</div>

                    <div className="options">
                      {currentQ.shuffledOptions.map((opt, idx) => {
                        let cls = 'option';
                        if (gradingMode === 'immediate' && revealed) {
                          if (opt.correct)           cls += ' correct-answer';
                          else if (idx === selected) cls += ' incorrect';
                          cls += ' disabled';
                        } else if ((gradingMode === 'exam' || gradingMode === 'timed') && selected !== null) {
                          if (idx === selected) cls += ' selected-exam';
                          cls += ' disabled';
                        }
                        return (
                          <button
                            key={idx}
                            className={cls}
                            onClick={() => handleSelect(idx)}
                            disabled={gradingMode === 'immediate' ? revealed : false}
                          >
                            <span className="opt-letter">{String.fromCharCode(65 + idx)}</span>
                            {opt.text}
                          </button>
                        );
                      })}
                    </div>

                    {gradingMode === 'immediate' && revealed && (
                      <>
                        <div className={`feedback ${currentQ.shuffledOptions[selected]?.correct ? 'correct' : 'incorrect'}`}>
                          <div className="feedback-title">
                            {currentQ.shuffledOptions[selected]?.correct ? '✓ Correct!' : '✗ Incorrect'}
                          </div>
                          <p>{currentQ.explanation}</p>
                          {currentQ.codeRef && (
                            <span className="code-ref">📖 {currentQ.codeRef}</span>
                          )}
                        </div>
                        <button className="btn-next" onClick={handleNext}>
                          {current + 1 >= displayLimit
                            ? (shouldGoToPaywallAtLimit ? 'Unlock Full Access →' : 'See Results')
                            : 'Next Question →'}
                        </button>
                      </>
                    )}

                    {(gradingMode === 'exam' || gradingMode === 'timed') && selected !== null && (
                      <button
                        className="btn-next"
                        onClick={current + 1 >= displayLimit
                          ? (shouldGoToPaywallAtLimit ? handleNext : handleSubmitExam)
                          : handleNext}
                      >
                        {current + 1 >= displayLimit
                          ? (shouldGoToPaywallAtLimit ? 'Unlock Full Access →' : 'Submit Quiz')
                          : 'Next Question →'}
                      </button>
                    )}
                  </div>

                  {(gradingMode === 'exam' || gradingMode === 'timed') && isFreeRun && current + 1 === displayLimit && selected !== null && (
                    <div className="exam-sticky-submit">
                      <button className="btn-next" onClick={handleSubmitExam}>
                        Submit Quiz
                      </button>
                    </div>
                  )}

                  <div className="quiz-nav-links">
                    {current > 0 && gradingMode === 'immediate' && (
                      <button className="quiz-nav-link" onClick={handlePrevious}>
                        ← Previous
                      </button>
                    )}
                    <button className="quiz-nav-link" onClick={restartCurrentQuiz}>
                      ↺ Restart Quiz
                    </button>
                    <button className="quiz-nav-link" onClick={resetToSetup}>
                      ⊞ Change Section
                    </button>
                    {session && (
                      <button className="flag-btn" onClick={() => { setShowFlagModal(true); setFlagSubmitted(false); }}>
                        🚩 Flag this question
                      </button>
                    )}
                  </div>

                  {/* FLAG MODAL */}
                  {showFlagModal && (
                    <div className="exam-confirm-overlay">
                      <div className="exam-confirm-dialog">
                        {flagSubmitted ? (
                          <>
                            <div className="exam-confirm-title">Thanks!</div>
                            <p className="exam-confirm-msg">We'll review this question and fix any issues.</p>
                            <button className="btn-retry" onClick={resetFlagState}>Done</button>
                          </>
                        ) : (
                          <>
                            <div className="exam-confirm-title">Flag this question</div>
                            <p className="exam-confirm-msg">What's the issue?</p>
                            <div className="flag-reasons">
                              {['Wrong answer', 'Outdated code reference', 'Typo / wording issue', 'Other'].map(r => (
                                <button
                                  key={r}
                                  className={`flag-reason-btn${flagReason === r ? ' selected' : ''}`}
                                  onClick={() => setFlagReason(r)}
                                >
                                  {r}
                                </button>
                              ))}
                            </div>
                            <textarea
                              className="flag-comment"
                              placeholder="Optional: add a note"
                              value={flagComment}
                              onChange={e => setFlagComment(e.target.value)}
                              rows={2}
                            />
                            <div className="exam-confirm-actions">
                              <button className="btn-retry" onClick={handleFlagSubmit} disabled={!flagReason || flagSubmitting}>
                                {flagSubmitting ? 'Submitting…' : 'Submit'}
                              </button>
                              <button className="btn-retry-outline" onClick={resetFlagState}>Cancel</button>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* TIMED EXAM CONFIRM */}
              {phase === 'timed-confirm' && examPreset && (
                <div className="question-card" style={{ textAlign: 'center', padding: '3rem 2.5rem' }}>
                  <div style={{ fontSize: '1.3rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--gold)', marginBottom: '0.75rem' }}>
                    Exam Simulator
                  </div>
                  <div style={{ fontSize: '3.5rem', marginBottom: '0.75rem' }}>⏱️</div>
                  <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: '2rem', color: 'var(--navy)', marginBottom: '0.4rem' }}>
                    {examPreset.questions} Questions · {formatExamDuration(examPreset.minutes)}
                  </h2>
                  <div style={{ color: 'var(--text-muted)', fontSize: '1rem', marginBottom: '1.5rem' }}>
                    🇨🇦 {LICENCE_LABELS[activeLicence]} — All Topics, Randomized
                  </div>
                  <div style={{ background: 'rgba(201,168,76,0.07)', border: '1px solid rgba(201,168,76,0.2)', borderRadius: '8px', padding: '1.1rem 1.5rem', marginBottom: '2rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0, fontSize: '0.98rem', color: 'var(--text-muted)', lineHeight: 2.1, textAlign: 'left' }}>
                      <li style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}><span style={{ color: 'var(--gold)', fontSize: '0.5rem' }}>●</span>Timer starts the moment you click Start.</li>
                      <li style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}><span style={{ color: 'var(--gold)', fontSize: '0.5rem' }}>●</span>No answer feedback until the simulation is complete.</li>
                      <li style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}><span style={{ color: 'var(--gold)', fontSize: '0.5rem' }}>●</span>Unanswered questions at time expiry count as incomplete.</li>
                    </ul>
                    <div style={{ width: '100%', textAlign: 'center', fontFamily: "'Playfair Display', serif", fontSize: '1.35rem', fontWeight: 600, color: 'var(--navy)' }}>
                      Good luck! 🍀
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.875rem' }}>
                    <button
                      className="btn-start"
                      style={{ width: 'auto', minWidth: '240px', justifyContent: 'center', padding: '16px 48px', fontSize: '1.15rem' }}
                      onClick={startTimedExam}
                    >
                      ▶ Start Simulation
                    </button>
                    <button
                      className="btn-retry-outline"
                      style={{ width: 'auto', minWidth: '240px', padding: '16px 48px', fontSize: '0.95rem' }}
                      onClick={resetToSetup}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* PAYWALL */}
              {phase === 'paywall' && (
                <div className="question-card paywall-card">
                  <div className="paywall-blurred">
                    <div className="q-meta">
                      <span className="q-num">Q{FREE_LIMIT + 1}</span>
                      <span className="q-topic">
                        {quizQuestions[FREE_LIMIT]?.topicLabel || 'Premium Content'}
                      </span>
                    </div>
                    <div className="q-text blurred-text">
                      {quizQuestions[FREE_LIMIT]?.question || 'Subscribe to unlock this question'}
                    </div>
                    <div className="options blurred-options">
                      {['A', 'B', 'C', 'D'].map(l => (
                        <div key={l} className="option disabled">
                          <span className="opt-letter">{l}</span>
                          <span className="blurred-dots">••••••••••••••••••</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="paywall-overlay">
                    <div className="paywall-inner">
                      <div className="paywall-lock">🔒</div>

                      <>
                        <div className="paywall-title">Create Your Account</div>
                        <div className="paywall-subtitle">
                          You've completed your {FREE_LIMIT} free questions for this section.<br />
                          Pick a plan, create your account, and continue to secure checkout.
                        </div>
                        <div className="signup-flow-header" style={{ width: '100%', marginBottom: '1rem' }}>
                          <div className="signup-flow-step">Step 1: Choose your plan</div>
                          <div className="signup-plan-picker" style={{ marginBottom: '0.75rem' }}>
                            <button
                              type="button"
                              className={`signup-plan ${paywallPlan === 'monthly' ? 'selected' : ''}`}
                              aria-pressed={paywallPlan === 'monthly'}
                              onClick={() => setPaywallPlan('monthly')}
                            >
                              <div className="signup-plan-name">Monthly</div>
                              <div className="signup-plan-price"><span className="price-was">$24.99</span> $9.99<span>/mo</span></div>
                            </button>
                            <button
                              type="button"
                              className={`signup-plan ${paywallPlan === 'annual' ? 'selected' : ''}`}
                              aria-pressed={paywallPlan === 'annual'}
                              onClick={() => setPaywallPlan('annual')}
                            >
                              <div className="signup-plan-name">Annual <span className="plan-badge">SAVE 34%</span></div>
                              <div className="signup-plan-price">$199.00<span>/yr</span></div>
                            </button>
                          </div>
                        </div>
                        <div className="paywall-pricing">
                          <span className="price-highlight"><span className="price-was">$24.99</span> $9.99</span>
                          <span className="price-period">/month</span>
                          <span className="price-or">or</span>
                          <span className="price-annual">$199.00/year</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            sessionStorage.setItem('trp_pending_checkout', 'paywall');
                            window.location.href = paywallCheckoutUrl;
                          }}
                          className={session ? 'upgrade-subtle-btn' : 'paywall-btn'}
                        >
                          {session ? 'Create Account & Continue →' : 'Create Account & Continue →'}
                        </button>
                        <div className="paywall-note">🔐 Secured by Stripe · Cancel anytime</div>
                        <div className="paywall-signin">
                          Already a subscriber? <Link to="/login">Sign In →</Link>
                        </div>
                      </>

                      <div style={{ width: '100%', borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: '0.75rem', marginBottom: '0.25rem' }}>
                        <button
                          type="button"
                          className="paywall-back"
                          onClick={() => { setShowPaywallPromo(p => !p); setPaywallPromoError(''); }}
                        >
                          {showPaywallPromo ? '▾ Hide promo code' : '▸ Have a promo code?'}
                        </button>
                        {showPaywallPromo && (
                          <div style={{ marginTop: '0.6rem' }}>
                            {paywallPromoError && (
                              <div style={{ fontSize: '0.78rem', color: '#e57373', marginBottom: '0.5rem' }}>
                                {paywallPromoError}
                              </div>
                            )}
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              <input
                                type="text"
                                value={paywallPromoCode}
                                onChange={e => { setPaywallPromoCode(e.target.value); setPaywallPromoError(''); }}
                                placeholder="Enter promo code"
                                autoFocus
                                style={{
                                  flex: 1, padding: '8px 12px', borderRadius: '6px',
                                  border: '1px solid rgba(201,168,76,0.4)',
                                  background: 'rgba(255,255,255,0.08)',
                                  color: '#fff', fontSize: '0.88rem',
                                  fontFamily: 'inherit', textTransform: 'uppercase',
                                }}
                              />
                              <button
                                className="paywall-btn"
                                onClick={handlePaywallPromo}
                                disabled={!paywallPromoCode.trim() || paywallPromoLoading}
                                style={{ width: 'auto', padding: '8px 14px', marginBottom: 0, fontSize: '0.85rem' }}
                              >
                                {paywallPromoLoading ? '…' : session ? 'Apply' : 'Go →'}
                              </button>
                            </div>
                            {!session && (
                              <div style={{ fontSize: '0.76rem', color: 'rgba(255,255,255,0.4)', marginTop: '0.4rem' }}>
                                You'll be taken to sign up with your code pre-filled.
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      <button className="paywall-back" onClick={resetToSetup}>
                        No thanks, go back
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* RESULTS */}
              {phase === 'results' && (
                <div className="results-card">
                  {gradingMode === 'timed' && examPreset && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', marginBottom: '1rem', background: 'rgba(201,168,76,0.07)', border: '1px solid rgba(201,168,76,0.2)', borderRadius: '8px', padding: '0.6rem 1rem', fontSize: '0.82rem', color: 'var(--gold)', fontWeight: 600, flexWrap: 'wrap' }}>
                        <span>⏱ Exam Simulation · {examPreset.questions} Questions · {formatExamDuration(examPreset.minutes)}</span>
                      {timeRemaining > 0 && <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>· Finished with {formatTime(timeRemaining)} to spare</span>}
                      {timeRemaining === 0 && <span style={{ color: '#e05252', fontWeight: 400 }}>· Time expired</span>}
                    </div>
                  )}
                  {showFullReview && (
                    <div className="exam-grade-header">
                      <div className="exam-grade-score">
                        {correctSoFar}/{answers.length} &mdash; {answers.length > 0 ? Math.round((correctSoFar / answers.length) * 100) : 0}%
                      </div>
                      <div className={`exam-grade-verdict ${correctSoFar / answers.length >= 0.7 ? 'pass' : 'fail'}`}>
                        {correctSoFar / answers.length >= 0.7 ? '✓ Pass' : '✗ Did Not Pass'}
                      </div>
                    </div>
                  )}
                  <div className="results-icon">
                    {correctSoFar / answers.length >= 0.7 ? '🏆' : '📚'}
                  </div>
                  <div className="results-score">
                    {answers.length > 0 ? Math.round((correctSoFar / answers.length) * 100) : 0}%
                  </div>
                  <div className="results-label">
                    {correctSoFar} correct out of {answers.length} questions
                  </div>
                  <div className="results-breakdown">
                    <div>
                      <div className="rb-num green">{correctSoFar}</div>
                      <div className="rb-label">Correct</div>
                    </div>
                    <div>
                      <div className="rb-num red">{gradingMode === 'timed' ? wrongCount : answers.length - correctSoFar}</div>
                      <div className="rb-label">{gradingMode === 'timed' ? 'Wrong' : 'Incorrect'}</div>
                    </div>
                    {gradingMode === 'timed' && (
                      <div>
                        <div className="rb-num" style={{ color: 'var(--gold)' }}>{incompleteCount}</div>
                        <div className="rb-label">Incomplete</div>
                      </div>
                    )}
                    <div>
                      <div className="rb-num navy">
                        {answers.length > 0 ? Math.round((correctSoFar / answers.length) * 100) : 0}%
                      </div>
                      <div className="rb-label">Score</div>
                    </div>
                  </div>
                  {!session && (
                    <p className="results-signin">
                      <Link to="/login">Sign in</Link> to save your scores to your dashboard.
                    </p>
                  )}
                  <button className="btn-retry" onClick={replayQuiz}>Try Again</button>
                  <button className="btn-retry-outline" onClick={resetToSetup}>Change Topic</button>
                  {!session && (
                    <div style={{ marginTop: '1rem' }}>
                      <button className="btn-retry" onClick={() => setModalOpen(true)}>
                        Subscribe for Full Access
                      </button>
                    </div>
                  )}

                  {showFullReview && (
                    <div className="exam-review">
                      <h3 className="exam-review-title">Full Review</h3>
                      {quizQuestions.map((q, i) => {
                        const ans = answers[i];
                        if (!ans) return null;
                        return (
                          <div key={i} className={`exam-review-question ${ans.correct ? 'review-correct' : ans.incomplete ? 'review-incomplete' : 'review-incorrect'}`}>
                            <div className="exam-review-header">
                              <span className="q-num">Q{i + 1}</span>
                              <span className={`exam-review-verdict ${ans.correct ? 'correct' : ans.incomplete ? 'incomplete' : 'incorrect'}`}>
                                {ans.correct ? '✓ Correct' : ans.incomplete ? '— Not Answered' : '✗ Incorrect'}
                              </span>
                            </div>
                            <div className="q-text" style={{ marginBottom: '0.75rem' }}>{q.question}</div>
                            <div className="options" style={{ pointerEvents: 'none' }}>
                              {q.shuffledOptions.map((opt, j) => {
                                let cls = 'option disabled';
                                if (opt.correct)           cls += ' correct-answer';
                                else if (j === ans.selectedIdx) cls += ' incorrect';
                                return (
                                  <button key={j} className={cls} disabled>
                                    <span className="opt-letter">{String.fromCharCode(65 + j)}</span>
                                    {opt.text}
                                  </button>
                                );
                              })}
                            </div>
                            <div className={`feedback ${ans.correct ? 'correct' : 'incorrect'}`} style={{ marginTop: '0.75rem' }}>
                              <p>{q.explanation}</p>
                              {q.codeRef && <span className="code-ref">📖 {q.codeRef}</span>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* EXAM SUBMIT CONFIRMATION */}
              {showSubmitConfirm && (
                <div className="exam-confirm-overlay">
                  <div className="exam-confirm-dialog">
                    <div className="exam-confirm-title">Submit Quiz?</div>
                    <p className="exam-confirm-msg">You won't be able to change your answers.</p>
                    <div className="exam-confirm-actions">
                      <button className="btn-retry" onClick={confirmSubmitExam}>Submit</button>
                      <button className="btn-retry-outline" onClick={() => setShowSubmitConfirm(false)}>Cancel</button>
                    </div>
                    {!hasFullAccess && current + 1 >= displayLimit && (
                      <div style={{ marginTop: '0.8rem', textAlign: 'center' }}>
                        <div style={{ fontSize: '0.78rem', opacity: 0.75, marginBottom: '0.45rem', letterSpacing: '0.04em' }}>
                          OR
                        </div>
                        <p className="exam-confirm-msg" style={{ marginBottom: '0.55rem', fontWeight: 700 }}>
                          Subscribe to unlock full quiz lengths.
                        </p>
                        <button
                          type="button"
                          onClick={() => {
                            sessionStorage.setItem('trp_pending_checkout', 'paywall');
                            window.location.href = monthlyCheckoutUrl;
                          }}
                          className={session ? 'upgrade-subtle-btn' : 'paywall-btn'}
                          style={{ width: 'auto', display: 'inline-block', marginBottom: 0 }}
                        >
                          {session ? 'Upgrade for Full Access →' : 'Unlock All Features →'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
            </>
          )}

          {/* ── QUICK QUIZ SUBSCRIBE CTA (non-subscribers only) ───────── */}
          {!hasFullAccess && ['oesc','ohsaquiz','regsquiz'].includes(activeTab) && (
            <div className="quick-quiz-cta">
              <div className="quick-quiz-cta-text">
                <strong>You're seeing 3 questions from a bank of hundreds.</strong>
                <p>
                  Subscribers draw 30 randomised questions every session — pulled from
                  real exam content covering every section, every trade, fully explained
                  with code references. A different set each time, so you're always
                  building knowledge rather than memorising answers.
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  sessionStorage.setItem('trp_pending_checkout', 'paywall');
                  window.location.href = monthlyCheckoutUrl;
                }}
                className={session ? 'upgrade-subtle-btn' : 'quick-quiz-cta-btn'}
              >
                {session ? 'Upgrade for Full Access →' : 'Unlock All Features →'}
              </button>
            </div>
          )}

          <Footer />
        </div>
      </div>

      {modalOpen && <SubscribeModal onClose={() => setModalOpen(false)} session={session} />}
    </>
  );
}

export default Quiz;
